import { AssigneeDisplay } from '../conversations/AssigneeDisplay';
import AssigneeSelector from '../conversations/AssigneeSelector';
import { useUser } from '../../providers/UserProvider';
import type { VirtualAppointmentTeam } from '../../services/virtualAppointments';
import { FieldLabel } from './VirtualAppointmentFieldParts';
import {
    NO_ASSIGNEE_LABEL,
    useVirtualAppointmentAssigneeName
} from './useVirtualAppointmentAssigneeName';

interface VirtualAppointmentAssigneeFieldProps {
    team: VirtualAppointmentTeam;
    /** '' for nobody. */
    assigneeEmail: string;
    onChange: (email: string) => void;
}

// Offers only the VA team's members: the patient is assigned to them when the VA fires.
export function VirtualAppointmentAssigneeField({
    team,
    assigneeEmail,
    onChange
}: VirtualAppointmentAssigneeFieldProps) {
    const { user } = useUser();
    return (
        <div className="flex flex-col">
            <FieldLabel>
                Assignee <span className="font-normal text-gray-400">(optional)</span>
            </FieldLabel>
            <div className="flex items-center gap-2 h-9">
                <AssigneeSelector
                    assignedTo={assigneeEmail || null}
                    roles={[team]}
                    onAssign={onChange}
                    onUnassign={() => onChange('')}
                    currentUserEmail={user?.email ?? ''}
                    dropdownAlign="left"
                    ariaLabel={`${team} assignee`}
                />
                {!assigneeEmail && (
                    <span className="text-xs text-gray-400">{NO_ASSIGNEE_LABEL}</span>
                )}
            </div>
        </div>
    );
}

interface VirtualAppointmentAssigneeValueProps {
    team: string;
    assigneeEmail: string | null;
}

// Read-only: the assignee's avatar and name.
export function VirtualAppointmentAssigneeValue({
    team,
    assigneeEmail
}: VirtualAppointmentAssigneeValueProps) {
    const name = useVirtualAppointmentAssigneeName(team, assigneeEmail);
    return (
        <AssigneeDisplay
            name={name}
            email={assigneeEmail}
            variant="avatar-name"
            unassignedLabel={NO_ASSIGNEE_LABEL}
        />
    );
}
